/// <amd-module name="Build/SourceProviders/GitHub" />

import { BaseGit } from "Build/SourceProviders/BaseGit";
import { IBranchInfo, ICommitInfo, IRepositoryInfo, RepositoryTypes, ICommitDiffInfo } from "Build/SourceProviders/Common";
import { ExternalUnknown } from "Build/SourceProviders/ExternalUnknown";
import * as Resources from "Build/SourceProviders/Resources";
import { IVCNavigationService } from "VC/Common/Navigation";

import { IVssPageContext } from "VSS/Platform/Context";
import { traceWarning } from "VSS/Platform/Trace";

import * as TraceNames from "TraceNames";

export class GitHub extends BaseGit {
    private _externalRepo: ExternalUnknown;
    private static s_GitHubPrefix = "https://github.com";

    public constructor(pageContext: IVssPageContext) {
        super(pageContext);
        this._externalRepo = new ExternalUnknown(pageContext);
    }

    public getKey(): string {
        return RepositoryTypes.GitHub;
    }

    public getName() {
        return Resources.GitHub;
    }

    public fetchBranchLink(info: IBranchInfo, projectName?: string): Promise<string | null> {
        return Promise.resolve(this._getBranchLinkInternal(info));
    }

    public fetchCommitLink(info: ICommitInfo, projectName?: string): Promise<string | null> {
        return Promise.resolve(this._getCommitLinkInternal(info));
    }

    public fetchRepositoryLink(info: IRepositoryInfo, projectName?: string): Promise<string | null> {
        return Promise.resolve(this._getRepositoryUrl(info.repositoryName));
    }

    public fetchPRCommitLink(info: IBranchInfo, projectName?: string): Promise<string | null> {
        if (info.repositoryName && info.branchName) {
            const prId = this._getPullRequestId(info.branchName);
            if (prId) {
                return Promise.resolve(`${this._getPullRequestUrl(prId, info.repositoryName)}/commits`);
            }
        }
        return this.emptyNullPromise;
    }

    public fetchCommitDiffLink(info: ICommitDiffInfo, projectName?: string): Promise<string | null> {
        let url = "";
        if (info.repositoryName && info.baseCommitId && info.targetCommitId) {
            url = `${this._getRepositoryUrl(info.repositoryName)}/compare/${info.baseCommitId}...${info.targetCommitId}`;
        }
        return Promise.resolve(url);
    }

    public getRepoIconName(): string {
        return "GitHubLogo";
    }

    public getMaxWorkItems(): number | undefined {
        return 10;
    }

    protected _getBranchLink(info: IBranchInfo, service: IVCNavigationService): string {
        return this._getBranchLinkInternal(info);
    }

    protected _getRepositoryLink(info: IRepositoryInfo, service: IVCNavigationService): string {
        return this._getRepositoryUrl(info.repositoryName);
    }

    protected _getCommitLink(info: ICommitInfo, service: IVCNavigationService): string {
        return this._getCommitLinkInternal(info);
    }

    private _getBranchLinkInternal(info: IBranchInfo): string {
        if (info.repositoryName && info.branchName) {
            const prId = this._getPullRequestId(info.branchName);
            if (prId) {
                return this._getPullRequestUrl(prId, info.repositoryName);
            }

            const normalizedBranchName = this._normalizeBranchName(info.branchName);
            return `${this._getRepositoryUrl(info.repositoryName)}/tree/${normalizedBranchName}`;
        }
        return this._externalRepo.getBranchLink(info);
    }

    private _getPullRequestUrl(pullRequestId: number, repositoryName: string) {
        return `${this._getRepositoryUrl(repositoryName)}/pull/${pullRequestId}`;
    }

    private _getCommitLinkInternal(info: ICommitInfo) {
        if (info.repositoryName && info.commitId) {
            return `${this._getRepositoryUrl(info.repositoryName)}/commit/${info.commitId}`;
        }
        return this._externalRepo.getCommitLink(info);
    }

    private _getRepositoryUrl(repositoryName: string): string {
        if (!repositoryName) {
            console.error("Name is required to create a link to the repository");
            return "";
        }

        // We ran servicing to convert all repository IDs to orgname/repo format, but some used to be URLs
        // See if we still get any URLs here. If not, we can remove this block
        if (repositoryName.startsWith(GitHub.s_GitHubPrefix)) {
            // repositoryName may contain EUII
            traceWarning(this.pageContext, TraceNames.area, "GitHub", "Data shape issue: repositoryName may be a URL");

            // the name could be the clone url, so remove ".git" if present
            return repositoryName.replace(/\.git$/g, "");
        }

        // assume repositoryName is "user/repo"
        const maxParts = 3;
        const parts = repositoryName.split("/", maxParts);
        if (parts.length !== 2 || !parts[0] || !parts[1]) {
            console.error(`Repository name "${repositoryName}" is not in the expected format ("user/repository")`);
            return "";
        }

        return `${GitHub.s_GitHubPrefix}/${repositoryName}`;
    }
}
